import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import { type TFunction } from 'i18next';
import { useMemo } from 'react';
import { type UseFormSetValue } from 'react-hook-form';
import { type Filter, FilterOrder, FilterSort } from '../../_types/Car';
import CustomPopover, { usePopover } from '../../components/custom-popover';
import Iconify from '../../components/iconify/component';
import { useRootContainerContext } from '../../context/root-container';
import { useSettingsContext } from '../../context/settings';
import { useResponsive } from '../../hooks/use-responsive';
import useLocales from '../../hooks/useLocales';
import { Modules } from '../../modules/_definition/types';
import checkUndefinedNullOrEmpty from '../../utils/tools/checkUndefinedNullOrEmpty';
import getStyleQuery from '../../utils/tools/getStyleQuery';

type Props = {
    filters: Filter;
    module: Modules;
    onFilters: UseFormSetValue<Filter>;
};

const rawSortOptions = (translate: TFunction, showRateFilter: boolean, showVerfuegbarAbFilter: boolean): { iSort: Filter['sort']; iOrder: Filter['order']; t: string }[] =>
    [
        { iSort: FilterSort.Views, iOrder: FilterOrder.DESC, t: translate('common:filter.views') },
        showRateFilter
            ? { iSort: FilterSort.Rate, iOrder: FilterOrder.ASC, t: `${translate('common:filter.rate')} ${translate('common:filter.asc')}` }
            : { iSort: FilterSort.Verkaufspreis, iOrder: FilterOrder.ASC, t: `${translate('common:filter.preis')} ${translate('common:filter.asc')}` },
        showRateFilter
            ? { iSort: FilterSort.Rate, iOrder: FilterOrder.DESC, t: `${translate('common:filter.rate')} ${translate('common:filter.desc')}` }
            : { iSort: FilterSort.Verkaufspreis, iOrder: FilterOrder.DESC, t: `${translate('common:filter.preis')} ${translate('common:filter.desc')}` },
        { iSort: FilterSort.Hersteller, iOrder: FilterOrder.ASC, t: `${translate('common:filter.hersteller')} ${translate('common:filter.asc')}` },
        { iSort: FilterSort.Hersteller, iOrder: FilterOrder.DESC, t: `${translate('common:filter.hersteller')} ${translate('common:filter.desc')}` },
        showVerfuegbarAbFilter ? { iSort: FilterSort.VerfugbarAb, iOrder: FilterOrder.ASC, t: `${translate('common:filter.availabileDate')} ${translate('common:filter.asc')}` } : undefined,
        showVerfuegbarAbFilter ? { iSort: FilterSort.VerfugbarAb, iOrder: FilterOrder.DESC, t: `${translate('common:filter.availabileDate')} ${translate('common:filter.desc')}` } : undefined,
    ]?.filter((f) => !checkUndefinedNullOrEmpty(f));

export default function CarListSort({ filters, onFilters, module }: Props) {
    const settings = useSettingsContext();
    const rootContainer = useRootContainerContext();
    const popover = usePopover();
    const { translate } = useLocales();
    const md = useResponsive(rootContainer, 'down', 'lg');

    const sortOptions = useMemo(
        () =>
            rawSortOptions(
                translate,
                module === Modules.Finanzierungsansicht ||
                    !checkUndefinedNullOrEmpty(filters?.fd) ||
                    !checkUndefinedNullOrEmpty(filters?.Rate) ||
                    !checkUndefinedNullOrEmpty(filters?.RateVon) ||
                    !checkUndefinedNullOrEmpty(filters?.RateBis),
                settings?.cartile?.badges?.includes('availableAt')
            ),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [filters?.fd, filters?.Rate, filters?.RateVon, filters?.RateBis, module, settings?.cartile?.badges]
    );
    const currentSort = sortOptions?.find((s) => s?.iSort === filters?.sort && s?.iOrder === filters?.order);

    return (
        <>
            {md ? (
                <Tooltip title={translate('common:carSearch.sort')}>
                    <IconButton data-testid="CarListSort_smallSortBtn" disabled={settings?.general?.mockup} color="primary" onClick={popover.onOpen}>
                        <Iconify icon="material-symbols:sort" />
                    </IconButton>
                </Tooltip>
            ) : (
                <Button
                    disableRipple
                    color="inherit"
                    disabled={settings?.general?.mockup}
                    onClick={popover.onOpen}
                    endIcon={<Iconify icon={popover.open ? 'solar:alt-arrow-up-line-duotone' : 'solar:alt-arrow-down-line-duotone'} />}
                    sx={{
                        [getStyleQuery()]: {
                            fontWeight: 'fontWeightSemiBold',
                        },
                    }}
                >
                    {translate('common:carSearch.sort')}:
                    <Box
                        component="span"
                        sx={{
                            [getStyleQuery()]: {
                                fontWeight: 'fontWeightBold',
                                ml: 0.5,
                                whiteSpace: 'nowrap',
                            },
                        }}
                    >
                        {currentSort?.t}
                    </Box>
                </Button>
            )}

            <CustomPopover open={popover.open} onClose={popover.onClose}>
                {sortOptions.map((option) => (
                    <MenuItem
                        key={`${option?.iSort}_${option?.iOrder}`}
                        selected={option?.iSort === filters?.sort && option?.iOrder === filters?.order}
                        onClick={() => {
                            popover.onClose();
                            onFilters('sort', option?.iSort);
                            onFilters('order', option?.iOrder);
                        }}
                    >
                        {option?.t}
                    </MenuItem>
                ))}
            </CustomPopover>
        </>
    );
}
